import React, { useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import { updateMapViewItem } from "../../../redux/mapView/mapViewSlice";
import {
  updateLiftSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
import MonitoringMapViewPin3 from "./MonitoringMapViewPin3";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// INTERFACES
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { MapViewDirections, MapViewTypes } from "../../../interfaces/MapView.interface";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import { defaultLiftPanelTab } from "../../../types/monitoringAccess";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { stringToUuidT } from "../../../utils/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";

function MonitoringMapViewWrapper({
  liftId,
  direction,
  text,
  x,
  y,
  type,
  layouts,
  liftType,
}: {
  liftId: string;
  direction: MapViewDirections;
  text: string;
  x: number;
  y: number;
  type: MapViewTypes;
  layouts: any;
  liftType: string;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [icon, setIcon] = React.useState<string>("elOutIndicator");
  const [zIndex, setZIndex] = React.useState(0);
  const [isVertical, setIsVertical] = React.useState(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { mapViewArray } = useSelector((state: RootState) => state.mapView);
  const { permissionSet } = useSelector((state: RootState) => state.user);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(liftId), [liftId]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = socketMessageLift.faults;

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getTabKey = () => {
    // Actions is the default tab for the drawer whenever it's available.
    if (hasPermission(Permission.MonitorControlPanelActions, permissionSet ?? [])) {
      return "ACTIONS";
    }
    if (hasFaults) {
      return "FAULTS";
    }
    return defaultLiftPanelTab(permissionSet ?? []);
  };

  const getLayoutAndLift = () => {
    const selectedLayout = layouts.find((layout: any) =>
      layout?.lifts.find((lift_: any) => lift_.id === liftId),
    );
    const selectedLift = selectedLayout?.lifts.find((lift_: any) => lift_.id === liftId);
    return { selectedLayout, selectedLift };
  };

  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  const onClickOpenModalElevator = (e: any) => {
    getDrawerPlacement(e);
    const { selectedLayout, selectedLift } = getLayoutAndLift();
    if (!selectedLayout || !selectedLift) return;
    dispatch(
      updateModalControlPanelTab({
        liftType: LiftType[LiftType.ELEVATOR],
        tab: getTabKey(),
      }),
    );
    dispatch(updateLiftSelected(selectedLift));
    dispatch(updateChannelId(selectedLayout.idChannel));
    dispatch(updateServerId(selectedLayout.idServer));
    dispatch(updateOpenModalControlPanelLift(true));
  };

  const handleClick = (e: any) => {
    if (type === MapViewTypes.MAP) {
      // get the selected map view data
      const selectedMapView = mapViewArray.find((mapView: any) => mapView.id === liftId);
      if (selectedMapView) {
        // update my element
        dispatch(updateMapViewItem(selectedMapView));
      }
    } else {
      onClickOpenModalElevator(e);
    }
  };

  const getDirection: () => "top" | "bottom" | "left" | "right" = () => {
    if (direction === MapViewDirections.UP) return "top";
    if (direction === MapViewDirections.DOWN) return "bottom";
    if (direction === MapViewDirections.LEFT) return "left";
    if (direction === MapViewDirections.RIGHT) return "right";
    return "top";
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (!socketMessageLift) return;
    // ICON
    if (socketMessageLift.direction === Usage.Up) {
      setIcon("elUp");
    } else if (socketMessageLift.direction === Usage.Down) {
      setIcon("elDown");
    }
    if (socketMessageLift.doorStatus.frontOpen && socketMessageLift.doorStatus.rearOpen) {
      setIcon("elFrontOpenRearOpen");
    } else if (!socketMessageLift.doorStatus.frontOpen && !socketMessageLift.doorStatus.rearOpen) {
      setIcon("elFrontCloseRearClose");
    } else if (socketMessageLift.doorStatus.frontOpen && !socketMessageLift.doorStatus.rearOpen) {
      setIcon("elFrontOpenRearClose");
    } else if (!socketMessageLift.doorStatus.frontOpen && socketMessageLift.doorStatus.rearOpen) {
      setIcon("elFrontCloseRearOpen");
    }
  }, [socketMessageLift]);

  React.useEffect(() => {
    if (direction === MapViewDirections.UP || direction === MapViewDirections.DOWN) {
      setIsVertical(true);
    } else if (direction === MapViewDirections.LEFT || direction === MapViewDirections.RIGHT) {
      setIsVertical(false);
    }
  }, [direction]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      style={{
        position: "absolute",
        top: `${y}px`,
        left: `${x}px`,
        zIndex,
      }}
      onClick={handleClick}
      onMouseEnter={() => setZIndex(99999)}
      onMouseLeave={() => setZIndex(0)}
      role="button"
      tabIndex={0}
      aria-label="map view wrapper"
      onKeyDown={handleClick}
    >
      <MonitoringMapViewPin3
        carName={text}
        icon={icon}
        direction={getDirection()}
        orientation={isVertical ? "vertical" : "horizontal"}
        liftId={liftId}
        liftType={liftType}
      />
    </div>
  );
}

export default MonitoringMapViewWrapper;
